import React from 'react';
import type { OrderItem } from '../../../types/apiTypes';
import { normalizeSku } from '../../../utils/normalizeSku';
import { compareShelfIndices, getBundleParentIds, isBundleParentRow } from './shelfUtils';
import { pickLineUnitCount } from './orderDataLogic';
import PickLineChip from './PickLineChip';
import {
  compareBundleWorkPickLocations,
  type BundleWorkPickLocation,
} from '@/shared/utils/bundleWorkPickLocation';

interface ComponentPickListProps {
  readonly orderItems: readonly OrderItem[];
  readonly skusWithNotes: Readonly<Record<string, true>>;
  readonly observeItem: (element: HTMLElement | null, id: string) => void;
  readonly onSelectItem: (item: OrderItem) => void;
  readonly onSelectShelf: (item: OrderItem) => void;
  readonly shelfScanEnabled: boolean;
  readonly onModify?: (item: OrderItem) => void;
  readonly header?: React.ReactNode;
  readonly locationSort?: 'order' | 'bundle-work-source';
}

/** One rendered pick row: the row item plus the ids React and the visibility observer key off. */
interface PickRow {
  readonly item: OrderItem;
  readonly reactKey: string;
  readonly observeId: string;
}

/**
 * Consolidation key for component rows. Unlike `normalizeSku` (uppercase + trim) this also
 * strips separators, so `RSV-1-4-3-8-converter` from two different bundles groups as one row.
 */
function consolidationKey(sku: string | undefined): string {
  return (sku || '').replace(/[\W_]+/g, '').toUpperCase();
}

function locationFromPickItem(item: OrderItem): BundleWorkPickLocation {
  const shelfIndex = item.shelfIndex && item.shelfIndex !== 'N/A' ? item.shelfIndex : '';
  const type = item.shelfDisplay && typeof item.shelfDisplay === 'object'
    ? 'stock'
    : (shelfIndex ? 'pick bin' : null);
  return { shelfIndex, type };
}

function comparePickRows(
  left: PickRow,
  right: PickRow,
  locationSort: 'order' | 'bundle-work-source',
): number {
  if (locationSort === 'bundle-work-source') {
    return compareBundleWorkPickLocations(
      locationFromPickItem(left.item),
      locationFromPickItem(right.item),
    );
  }
  return compareShelfIndices(left.item.shelfIndex, right.item.shelfIndex);
}

/** True when puzzle mode hides bundle parents or rolls duplicate pick lines into fewer chips. */
export function orderBenefitsFromPuzzleView(orderItems: readonly OrderItem[]): boolean {
  const parentIds = getBundleParentIds([...orderItems]);
  const hasBundleParents = orderItems.some((item) => isBundleParentRow(item, parentIds));
  const pickLines = orderItems.filter((item) => !isBundleParentRow(item, parentIds));
  const consolidatedSkuCount = new Set(pickLines.map((item) => consolidationKey(item.sku))).size;
  const hasSkuRollup = pickLines.length > consolidatedSkuCount;
  return hasBundleParents || hasSkuRollup;
}

/** Bundle parent rows dropped; same-SKU lines collapse into one item with summed `totalQty`. */
export function consolidatePickLines(orderItems: readonly OrderItem[]): OrderItem[] {
  const parentIds = getBundleParentIds([...orderItems]);
  const pickLines = orderItems.filter((item) => !isBundleParentRow(item, parentIds));
  const bySku: Record<string, OrderItem[]> = {};
  pickLines.forEach((item) => {
    const key = consolidationKey(item.sku);
    if (!bySku[key]) {
      bySku[key] = [];
    }
    bySku[key].push(item);
  });

  return Object.values(bySku).map((group) => {
    const first = group[0];
    const totalQty = group.reduce((sum, item) => sum + pickLineUnitCount(item), 0);
    const allScanned = group.every((item) => item.scanned);
    const scanPulse = group.reduce((max, line) => Math.max(max, line.scanPulse ?? 0), 0);
    const displayName = totalQty > 1
      ? `${(first.name || '').replace(/\s*\(\d+\s*units?\)\s*$/i, '').trim()} (${totalQty} units)`
      : first.name;
    return {
      id: `consolidated-${first.sku}`,
      sku: first.sku,
      name: displayName,
      shelfIndex: first.shelfIndex,
      shelfDisplay: first.shelfDisplay,
      scanned: allScanned,
      firstItemId: first.id,
      totalQty,
      isConsolidatedComponent: true,
      isComponent: group.some((line) => line.isComponent),
      scanPulse,
    };
  });
}

function toPickRow(item: OrderItem): PickRow {
  return {
    item,
    reactKey: `consolidated-${item.sku}-${item.scanPulse ?? 0}`,
    observeId: item.firstItemId ?? item.id,
  };
}

/**
 * Components-only ("puzzle") pick list: bundle parent rows are hidden; every other pick line
 * is consolidated by SKU into one `(N units)` chip. A single scan in puzzle mode clears all
 * lines sharing that SKU.
 */
function ComponentPickList({
  orderItems,
  skusWithNotes,
  observeItem,
  onSelectItem,
  onSelectShelf,
  shelfScanEnabled,
  onModify,
  header,
  locationSort = 'order',
}: ComponentPickListProps): React.ReactElement {
  const itemsToShow: PickRow[] = consolidatePickLines(orderItems)
    .map(toPickRow)
    .sort((a, b) => comparePickRows(a, b, locationSort));

  return (
    <div className="items-container">
      {header}
      <ul className="items-list">
        {itemsToShow.map(({ item, reactKey, observeId }) => {
          const hasWarehouseNote = Boolean(item.sku && skusWithNotes[normalizeSku(item.sku)]);

          return (
            <li
              key={reactKey}
              ref={(el) => observeItem(el, observeId)}
              className={`item ${item.scanned ? 'scanned' : ''}`}
            >
              <PickLineChip
                item={item}
                hasWarehouseNote={hasWarehouseNote}
                onSelectProduct={onSelectItem}
                onSelectShelf={onSelectShelf}
                shelfScanEnabled={shelfScanEnabled}
                onModify={onModify}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ComponentPickList;
